<template>
    <div class="inline-block">
        <input-label v-if="label" :for_element="name"> {{ $t(label) }}</input-label>

        <div class="flex items-center field-multi"
            :class="{ 'read_only': read_only }"
        >
            <div class="w-9">
                <input type="text"
                    :id="input_id"
                    class="field-multi-item w-full text-center"
                    :class="{ 'read_only': read_only }"
                    maxlength="2"
                    :name="name + '[month]'"
                    placeholder="mm"
                    v-bind="$attrs"
                    v-model="month"
                    :readonly="read_only"
                    :disabled="read_only"
                    ref="month"
                    @paste.prevent="paste"
                    @keypress="isNumber('month')"
                />
            </div>

            <div class="text-gray-600"
                :style="{
                    backgroundColor: read_only ? '#f7f7f7 !important' : 'transparent'
                }"
            >
                /
            </div>

            <div class="w-9">
                <input type="text"
                    class="field-multi-item w-full text-center"
                    :class="{ 'read_only': read_only }"
                    maxlength="2"
                    :name="name + '[day]'"
                    placeholder="dd"
                    v-bind="$attrs"
                    v-model="date"
                    :readonly="read_only"
                    :disabled="read_only"
                    ref="date"
                    @keyup.delete="deleteDate"
                    @paste.prevent="paste"
                    @keypress="isNumber('date')"
                />
            </div>

            <div class="text-gray-600"
                :style="{
                    backgroundColor: read_only ? '#f7f7f7 !important' : 'transparent'
                }"
            >
                /
            </div>

            <div class="w-14">
                <input type="text"
                    class="field-multi-item w-full text-center"
                    :class="{ 'read_only': read_only }"
                    maxlength="4"
                    :name="name + '[year]'"
                    placeholder="yyyy"
                    v-bind="$attrs"
                    v-model="year"
                    @change="fixTwoDigitYear"
                    :readonly="read_only"
                    :disabled="read_only"
                    ref="year"
                    @keyup.delete="deleteYear"
                    @paste.prevent="paste"
                    @keypress="isNumber('year')"
                />
            </div>

            <div v-if="! read_only"
                class="relative flex self-stretch items-center justify-center"
            >
                <button type="button"
                    class="flex h-full items-center justify-center w-10 border-l border-gray-400 transition-colors"
                    :class="show_datepicker
                        ? 'text-green-warm-700 bg-green-warm-100'
                        : 'text-gray-500 hover:text-gray-600 focus:text-gray-600 hover:bg-gray-200 focus:bg-gray-200'
                    "
                    @click="showDatepicker"
                >
                    <i class="far fa-calendar-alt" aria-hidden="true"></i>
                </button>

                <div class="absolute shadow-lg top-0 right-0 mt-12 z-100"
                    v-if="show_datepicker"
                    v-click-outside="onClickOutside"
                >
                    <date-picker
                        v-model="datepicker_value"
                        type="date"
                        :clearable="false"
                        :editable="false"
                        :inline="true"
                        @change="handleDatePickerChange"
                    >
                        <template v-slot:footer="{ emit }">
                            <div style="text-align: left">
                                <button class="mx-btn mx-btn-text" @click="selectToday(emit)">
                                    Today
                                </button>
                            </div>
                        </template>
                    </date-picker>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DateFunctions from '../mixins/date-functions.js';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import * as dayjs from 'dayjs';

export default {
    inheritAttrs: false,

    mixins: [
        DateFunctions,
    ],

    components: {
        DatePicker,
    },

    props: {
        label: {},
        name: {},
        value: {},
        input_id: {},
        read_only: { default: false },
    },

    data() {
        return {
            month_entry: this.parseStringDate(this.value).month,
            date_entry: this.parseStringDate(this.value).date,
            year_entry: this.parseStringDate(this.value).year,
            previous_value_month: 0,
            previous_value_date: 0,
            previous_value_year: 0,

            datepicker_value: this.getValueForDatepicker(),
            show_datepicker: false,
        }
    },

    computed: {
        month: {
            get() {
                return this.month_entry === null ? this.month_from_prop : this.month_entry;
            },
            set(val) {
                if (val.length == 2) {
                    this.$refs.date.focus();
                }

                this.previous_value_month = this.month_entry;
                this.month_entry = val;
                this.$emit('entered', { month: val });
            },
        },
        date: {
            get() {
                return this.date_entry === null ? this.date_from_prop : this.date_entry;
            },
            set(val) {
                if (val.length == 2) {
                    this.$refs.year.focus();
                }

                this.previous_value_date = this.date_entry;
                this.date_entry = val;
                this.$emit('entered', { date: val });
            },
        },
        year: {
            get() {
                return this.year_entry === null ? this.year_from_prop : this.year_entry;
            },
            set(val) {
                this.previous_value_year = this.year_entry;
                this.year_entry = val;
                this.$emit('entered', { year: val });
            },
        },
        date_object_from_prop() {
            return this.parseStringDate(this.value);
        },
        month_from_prop() {
            return this.date_object_from_prop.month;
        },
        date_from_prop() {
            return this.date_object_from_prop.date;
        },
        year_from_prop() {
            return this.date_object_from_prop.year;
        },
        date_object_from_entry() {
            return ('0000' + (this.year_entry || '')).slice(-4)
                + '-' + ('00' + (this.month_entry || '')).slice(-2)
                + '-' + ('00' + (this.date_entry || '')).slice(-2);
        },
    },

    methods: {
        onClickOutside() {
            this.show_datepicker = false;
        },
        handleDatePickerChange(value, type) {
            if (type === 'date') {
                this.show_datepicker = false;
                this.updateEntries(dayjs(value).format('YYYY-MM-DD'));
                this.$emit('entered', {
                    date: this.date,
                    month: this.month,
                    year: this.year,
                });
            }
        },
        getValueForDatepicker() {
            return this.value
                ? dayjs(this.value.substring(0, 10)).toDate()
                : null;
        },
        showDatepicker() {
            if (this.show_datepicker) {
                this.show_datepicker = false;
            } else {
                this.datepicker_value = this.getValueForDatepicker();
                this.show_datepicker = true;
            }
        },
        selectToday(emit) {
            this.handleDatePickerChange(new Date(), 'date')
        },
        isNumber(field) {
            const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',];

            if (event.key === '/') {
                event.preventDefault();
                switch (field) {
                    case 'month':
                        this.$refs.date.focus()
                        break;
                    case 'date':
                        this.$refs.year.focus()
                        break;
                }
            } else if (! keysAllowed.includes(event.key)) {
                event.preventDefault();
            }
        },
        updateEntries(value) {
            let prop = this.parseStringDate(value);

            if (prop.year != this.year_entry) {
                this.year_entry = prop.year;
            }

            if (prop.month != this.month_entry) {
                this.month_entry = prop.month;
            }

            if (prop.date != this.date_entry) {
                this.date_entry = prop.date;
            }
        },
        fixTwoDigitYear() {
            if (this.year_entry && this.year_entry < 100) {
                this.year_entry = parseInt(this.year_entry) + 2000;
            }
        },
        deleteDate() {
            if (this.date_entry) {
                return;
            }

            if (this.previous_value_date) {
                this.previous_value_date = null;
            } else {
                this.$refs.month.focus();
            }
        },
        deleteYear() {
            if (this.year_entry) {
                return;
            }

            if (this.previous_value_year) {
                this.previous_value_year = null;
            } else {
                this.$refs.date.focus();
            }
        },
        paste(event) {
            let pasted = event.clipboardData.getData('text/plain');
            let parsed = this.parseDisplayDateToObject(pasted);

            this.month = parsed.month;
            this.date = parsed.date;
            this.year = parsed.year;
        },
    },

    watch: {
        date_object_from_entry(val) {
            this.$emit('input', val);
        },
        value(val) {
            this.updateEntries(val);
        },
    },
}
</script>

<style scoped>
    .read_only {
        background-color: #f7f7f7 !important;
        border-color: #f7f7f7 !important;
        color: #262626 !important;
    }

    .accordion .read_only {
        background-color: #f7f7f7 !important;
        border-color: #d8d8d8 !important;
        color: #464646 !important;
    }

    .read_only::placeholder {
        color: transparent !important;
    }
</style>
